import { ApiServiceBase } from './base/api-service-base';
import {
	GrowthModelChangeLogEntry,
	GrowthModelChangeLogPage,
	GrowthModelChangeLogQuery,
	GrowthModelCloneVersionPayload,
	GrowthModelCloneVersionResult,
	GrowthModelDraftEdits,
	GrowthModelExportPayload,
	GrowthModelPublishPayload,
	GrowthModelPublishResult,
	GrowthModelUser,
	GrowthModelVersionChangeResult,
	GrowthModelWorkbook,
} from '../../models/growth-model/growth-model';
import { sanitizeDraftEditsForApi } from '../../components/pages/GrowthModel/sanitize-draft-edits-for-api';

export interface IGrowthModelApiService {
	getModel(planVersionId?: string): Promise<GrowthModelWorkbook>;
	getUsers(): Promise<GrowthModelUser[]>;
	getChangeLog(query: GrowthModelChangeLogQuery): Promise<GrowthModelChangeLogPage>;
	getChangeLogBatch(batchId: string, query: GrowthModelChangeLogQuery): Promise<GrowthModelChangeLogEntry[]>;
	bindActuals(planVersionId?: string): Promise<{ workbook: GrowthModelWorkbook; bindSource: string; modelRevisionId?: string; planVersionId?: string }>;
	saveDraft(edits: GrowthModelDraftEdits, planVersionId?: string): Promise<void>;
	evaluate(payload?: { modelRevisionId?: string; edits?: GrowthModelDraftEdits; cutoffIdx?: number | null }): Promise<any>;
	publishVersion(payload: GrowthModelPublishPayload & { edits?: GrowthModelDraftEdits; cutoffIdx?: number | null; planVersionId?: string }): Promise<GrowthModelPublishResult>;
	cloneVersion(payload: GrowthModelCloneVersionPayload): Promise<GrowthModelCloneVersionResult>;
	renameVersion(planVersionId: string, name: string): Promise<GrowthModelVersionChangeResult>;
	deleteVersion(planVersionId: string): Promise<GrowthModelVersionChangeResult>;
	exportWorkbook(payload: GrowthModelExportPayload): Promise<GrowthModelExportPayload>;
}

const baseEndpoint = 'growth-model';

export function changeLogQueryString(query: GrowthModelChangeLogQuery): string {
	const params = new URLSearchParams();
	Object.entries(query).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
	});
	const qs = params.toString();
	return qs ? `?${qs}` : '';
}

export function versionQueryString(planVersionId?: string): string {
	return planVersionId ? `?planVersionId=${encodeURIComponent(planVersionId)}` : '';
}

export function mapPublishResult(result: any, fallbackName: string): GrowthModelPublishResult {
	const workingCutoffIdx = result?.workingCutoffIdx ?? result?.WorkingCutoffIdx;
	return {
		id: String(result?.id || result?.Id || ''),
		name: result?.name || result?.Name || fallbackName,
		at: result?.at || result?.At || new Date().toISOString(),
		snowflakeRows: result?.snowflakeRows ?? result?.SnowflakeRows,
		bindSource: result?.bindSource || result?.BindSource,
		snowflakeTable: result?.snowflakeTable || result?.SnowflakeTable,
		planVersionId: result?.planVersionId || result?.PlanVersionId || undefined,
		workingCutoffIdx: typeof workingCutoffIdx === 'number' ? workingCutoffIdx : null,
		snowflakeWarning: result?.snowflakeWarning || result?.SnowflakeWarning || null,
	};
}

export function mapCloneResult(result: any, fallbackName: string): GrowthModelCloneVersionResult {
	return {
		planVersionId: String(result?.planVersionId || result?.PlanVersionId || ''),
		name: result?.name || result?.Name || fallbackName,
		cutoffPeriod: Number(result?.cutoffPeriod ?? result?.CutoffPeriod ?? 0),
	};
}

export function mapVersionChangeResult(result: any, planVersionId: string, fallbackName = ''): GrowthModelVersionChangeResult {
	return {
		planVersionId: String(result?.planVersionId || result?.PlanVersionId || planVersionId),
		name: result?.name || result?.Name || fallbackName,
		snowflakeWarning: result?.snowflakeWarning || result?.SnowflakeWarning || null,
	};
}

export class GrowthModelApiService extends ApiServiceBase implements IGrowthModelApiService {
	public async getModel(planVersionId?: string): Promise<GrowthModelWorkbook> {
		const model = await this.GetFromApi<GrowthModelWorkbook>(`${baseEndpoint}/model${versionQueryString(planVersionId)}`);
		if (!model?.months?.length) {
			throw new Error('Growth Model returned an empty workbook');
		}
		return model;
	}

	public async getUsers(): Promise<GrowthModelUser[]> {
		return (await this.GetFromApi<GrowthModelUser[]>(`${baseEndpoint}/users`)) || [];
	}

	public async getChangeLog(query: GrowthModelChangeLogQuery): Promise<GrowthModelChangeLogPage> {
		const page = await this.GetFromApi<GrowthModelChangeLogPage>(`${baseEndpoint}/change-log${changeLogQueryString(query)}`);
		return { total: page?.total || 0, changes: page?.changes || 0, items: page?.items || [] };
	}

	public async getChangeLogBatch(batchId: string, query: GrowthModelChangeLogQuery): Promise<GrowthModelChangeLogEntry[]> {
		const path = `${baseEndpoint}/change-log/${encodeURIComponent(batchId)}${changeLogQueryString(query)}`;
		return (await this.GetFromApi<GrowthModelChangeLogEntry[]>(path)) || [];
	}

	public async bindActuals(planVersionId?: string): Promise<{ workbook: GrowthModelWorkbook; bindSource: string; modelRevisionId?: string; planVersionId?: string }> {
		const result = await this.PostToApi<any>(`${baseEndpoint}/bind-actuals${versionQueryString(planVersionId)}`, {});
		if (!result?.workbook?.months?.length) {
			throw new Error('bind-actuals returned an empty workbook');
		}
		return {
			workbook: result.workbook,
			bindSource: result.bindSource || result.workbook.bindSource || 'unknown',
			modelRevisionId: result.modelRevisionId || result.workbook.modelRevisionId,
			planVersionId: result.planVersionId || result.workbook.planVersionId,
		};
	}

	public async saveDraft(edits: GrowthModelDraftEdits, planVersionId?: string): Promise<void> {
		await this.PostToApi(`${baseEndpoint}/draft${versionQueryString(planVersionId)}`, sanitizeDraftEditsForApi(edits));
	}

	public async evaluate(payload: { modelRevisionId?: string; edits?: GrowthModelDraftEdits; cutoffIdx?: number | null } = {}): Promise<any> {
		return await this.PostToApi(`${baseEndpoint}/evaluate`, payload);
	}

	public async publishVersion(
		payload: GrowthModelPublishPayload & { edits?: GrowthModelDraftEdits; cutoffIdx?: number | null; planVersionId?: string }
	): Promise<GrowthModelPublishResult> {
		const result = await this.PostToApi<any>(`${baseEndpoint}/publish`, payload, 'json', 120000);
		return mapPublishResult(result, payload.name);
	}

	public async cloneVersion(payload: GrowthModelCloneVersionPayload): Promise<GrowthModelCloneVersionResult> {
		const result = await this.PostToApi<any>(`${baseEndpoint}/versions/clone`, payload, 'json', 120000);
		return mapCloneResult(result, payload.name);
	}

	public async renameVersion(planVersionId: string, name: string): Promise<GrowthModelVersionChangeResult> {
		const result = await this.PutToApi<any>(`${baseEndpoint}/versions/${encodeURIComponent(planVersionId)}`, { name });
		return mapVersionChangeResult(result, planVersionId, name);
	}

	public async deleteVersion(planVersionId: string): Promise<GrowthModelVersionChangeResult> {
		const result = await this.DeleteFromApi<any>(`${baseEndpoint}/versions/${encodeURIComponent(planVersionId)}`);
		return mapVersionChangeResult(result, planVersionId);
	}

	public async exportWorkbook(payload: GrowthModelExportPayload): Promise<GrowthModelExportPayload> {
		return payload;
	}
}
